import axios from 'axios';
import {capitalize, get, includes, isEmpty} from 'lodash'
import {
  FC,
  PropsWithChildren,
  createContext,
  useEffect,
  useState,
  useCallback,
  useContext,
  useRef,
} from 'react'
import toast from 'react-hot-toast'
import {jwtDecode} from "jwt-decode";
import Axios from '../axios'
import Auth, { EmbedMetadata, EmbedToken } from '../interfaces/Auth'
import User, { InvoicePaymentStatus } from '../interfaces/User'
import * as keycloak from '../keycloak'
import { getPosToken, getUser } from '../services/user'
import CasaLoader from '../shared/components/CasaLoader/CasaLoader'
import PaymentRestriction from '../shared/components/PaymentRestriction/PaymentRestriction'
import AppError from '../error/AppError/AppError'
import AccessRestriction from '../shared/components/AccessRestriction/AccessRestriction'
import { UserProvider } from './User'
import {posRoutePages} from "../routes";
import { doesRouteTokenAuthenticated, isPublicRoute } from '../services/auth'
import { saveConfig } from '../utils/indexDB';
import { getRestriction } from '../utils/restriction';
import { updateTenant } from '../features/TenantSelection/services/api';
import { broadcastTenantChange, initTenantSync } from '../utils/tenantSync';
import { useTranslations } from 'use-intl';
import LocaleContext from './Locale';

const AuthContext = createContext<Auth>({
  currentUser: null,
  updateUserTenant: async () => {},
  embedMetadata: null,
});

export const AuthProvider: FC<PropsWithChildren> = ({children}) => {
  const t = useTranslations()
  const tRef = useRef(t)
  tRef.current = t
  const { setLocale } = useContext(LocaleContext)
  const [isLoading, setIsLoading] = useState(true)
  const [isRouteAuthenticated, setIsRouteAuthenticated] = useState(false)
  const [currentUser, setCurrentUser] = useState<User>()
  const [embedMetadata, setEmbedMetadata] = useState<EmbedMetadata | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isPaymentOverdue, setIsPaymentOverdue] = useState(false)
  const [overdueInvoicePaymentStatus, setOverdueInvoicePaymentStatus] =
    useState<InvoicePaymentStatus | null>(null)
  // TENANT_INACTIVE / NO_TENANT_ACCESS only — BUSINESS_INACTIVE is handled inside the dashboard
  // layout (Dashboard.tsx), once a business is actually selected.
  const [accessRestriction, setAccessRestriction] = useState<{
    reason: 'TENANT_INACTIVE' | 'NO_TENANT_ACCESS';
    isInternalUser: boolean;
    tenantName?: string;
    userEmail?: string;
  } | null>(null)

  // A tenant always has at least one business unless its creation went wrong; nothing downstream
  // (selectedBusiness, formatCurrency, …) can render without one, so stop here with a support hint.
  const [hasNoBusiness, setHasNoBusiness] = useState(false)

  const isPosRoute = useCallback((pathName: string) => {
    for (const route of posRoutePages) {
      if (route.path === pathName) {
        return true;
      }
    }
    return false
  }, [])

  // Setup cross-tab tenant change listener
  useEffect(() => {
    const cleanup = initTenantSync(() => {
      // Another tab switched tenant 
      console.log('Reloading due to tenant change in another tab');
      window.location.reload();
    });

    return cleanup;
  }, []);

  // Function to update tenant and refresh user
  const updateUserTenant = useCallback(async (tenantId: number) => {
    try {
      await updateTenant(tenantId);      
      broadcastTenantChange();      
      const user = await getUser();
      setCurrentUser(user);
      saveConfig("notification", { soundEnabled: user.notification_sound_enabled });
      if (user.locale) setLocale(user.locale);
      toast.success(`Switched to ${capitalize(user.tenant.displayName)}`);
      window.location.reload();
    } catch (error) {
      console.error('Failed to update tenant:', error);
      toast.error('Tenant switch failed');
      // throw error;
    }
  }, [setLocale]);

  // Initial authentication and user fetch
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        const currentPath = window.location.pathname;

        // Public pages have no session to establish: render them straight away
        // so keycloak.initialize() never runs and never redirects to login.
        if (isPublicRoute(currentPath)) {
          setIsRouteAuthenticated(true);
          return;
        }

        // Handle POS route authentication
        if (isPosRoute(currentPath) && includes(window.location.href, "loadTokenFromUrl")) {
          const urlSearchParams = new URLSearchParams(window.location.search);
          const decoded: string | null = urlSearchParams.get("token");

          if (decoded !== null) {
            const embedToken = await getPosToken(decoded)
              .then((response) =>
                jwtDecode<EmbedToken>(get(response, "token"))
              )
            const token = embedToken?.token;
            if (token !== undefined) {
              Axios.init({ token: token });
              setEmbedMetadata({ customerMobile: embedToken?.data?.mobile });
              const user = await getUser();
              saveConfig("notification", { soundEnabled: user.notification_sound_enabled });
              if (user.locale) setLocale(user.locale);
              setCurrentUser(user);
              return;
            }
          }
        }
        
        // Handle route-based authentication
        const doesRouteAuthenticated = doesRouteTokenAuthenticated()
        if (doesRouteAuthenticated) {
          setIsRouteAuthenticated(true);
          return;
        }

        const isAuthenticated = await keycloak.initialize();

        if (!isAuthenticated) {
          keycloak.redirectToLogin();
          return;
        }

        const accessToken = keycloak.getAccessToken();

        if (!accessToken) {
          throw new Error('Authentication failed');
        }

        axios.defaults.headers.common[
          'Authorization'
        ] = `Bearer ${accessToken}`;
        
        // Fetch user without tenantId - backend returns user with their selected tenant
        const user = await getUser();
        if (!user.tenant?.brands?.some((brand) => brand.businesses?.length)) {
          setHasNoBusiness(true);
          return;
        }
        saveConfig("notification", { soundEnabled: user.notification_sound_enabled });
        if (user.locale) setLocale(user.locale);
        setCurrentUser(user);

        toast.success(tRef.current('common.welcomeBack', { name: capitalize(user.tenant.displayName) }));
      } catch (error) {
        // `getRestriction` reads the shared restriction contract (status + details.code) and
        // still understands the older bare-402 shape, so this build works against an api-server
        // on either side of that change.
        const restriction = getRestriction(error);
        if (restriction?.code === 'PAYMENT_OVERDUE') {
          setIsPaymentOverdue(true);
          setOverdueInvoicePaymentStatus(restriction.invoicePaymentStatus ?? null);
        } else if (restriction?.code === 'TENANT_INACTIVE' || restriction?.code === 'NO_TENANT_ACCESS') {
          setAccessRestriction({
            reason: restriction.code,
            isInternalUser: restriction.isInternalUser ?? false,
            tenantName: restriction.tenantName,
            userEmail: keycloak.getEmail(),
          });
        } else {
          setError((error as Error).message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [isPosRoute, setLocale]);

  if (isLoading) return <CasaLoader />
  if (isPaymentOverdue)
    return <PaymentRestriction invoicePaymentStatus={overdueInvoicePaymentStatus} />
  if (accessRestriction)
    return (
      <AccessRestriction
        reason={accessRestriction.reason}
        isInternalUser={accessRestriction.isInternalUser}
        tenantName={accessRestriction.tenantName}
        userEmail={accessRestriction.userEmail}
      />
    )
  if (hasNoBusiness)
    return (
      <AppError
        description={t('errors.app.noBusiness')}
        showLogout
      />
    )
  if (error) return <AppError message={error} />
  if (isRouteAuthenticated) return <>{children}</>
  if (isEmpty(currentUser)) return <AppError />


  return (
    <AuthContext.Provider value={{ currentUser, updateUserTenant, embedMetadata }}>
      <UserProvider currentUser={currentUser}>{children}</UserProvider>
    </AuthContext.Provider>
  )
}

export default AuthContext
